<template>
  <el-container class="admin-page">
    <el-header class="admin-page__title">
      Bug 管理
    </el-header>

    <el-main>
      <div class="admin-page__toolbar">
        <el-input
          v-model="searchQuery"
          placeholder="输入Bug ID或标题进行搜索"
          :style="{ width: isMobile ? '100%' : '300px' }"
          clearable
          @input="handleSearch"
        >
          <template #prefix>
            <el-icon><Search /></el-icon>
          </template>
        </el-input>
      </div>

      <!-- PC端表格视图 -->
      <el-table 
        v-if="!isMobile"
        :data="bugData" 
        style="width: 100%"
        stripe
        v-loading="loading"
      >
        <el-table-column prop="bug_id" label="Bug ID" width="150" />
        <el-table-column prop="title" label="标题" min-width="200" />
        <el-table-column prop="status" label="状态" width="100" />
        <el-table-column prop="priority" label="优先级" width="100" />
        <el-table-column label="操作" width="150" fixed="right">
          <template #default="scope">
            <el-button type="primary" size="small" @click="viewBug(scope.row)">查看</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 移动端卡片视图 -->
      <div v-else class="mobile-cards" v-loading="loading">
        <el-card v-for="bug in bugData" :key="bug.bug_id" class="mobile-card">
          <div class="card-item">
            <span class="label">Bug ID：</span>
            <span class="value">{{ bug.bug_id }}</span>
          </div>
          <div class="card-item">
            <span class="label">标题：</span>
            <span class="value">{{ bug.title }}</span>
          </div>
          <div class="card-item">
            <span class="label">状态：</span>
            <el-tag size="small" :type="getStatusType(bug.status)">
              {{ bug.status }}
            </el-tag>
          </div>
          <div class="card-item">
            <span class="label">优先级：</span>
            <el-tag size="small" :type="getPriorityType(bug.priority)">
              {{ bug.priority }}
            </el-tag>
          </div>
          <div class="card-actions">
            <el-button type="primary" size="small" @click="viewBug(bug)">
              查看详情
            </el-button>
          </div>
        </el-card>
      </div>

      <!-- 分页器 -->
      <el-pagination
        class="admin-page__pager"
        background
        :layout="isMobile ? 'prev, pager, next' : 'total, sizes, prev, pager, next, jumper'"
        :total="totalCount"
        :page-size="pageSize"
        :current-page="currentPage"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </el-main>
  </el-container>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { ElMessage } from 'element-plus';
import { useWindowSize } from '@vueuse/core';
import { Search } from '@element-plus/icons-vue';
import authApi from '@/js/connect';

const { width } = useWindowSize();
const isMobile = computed(() => width.value <= 768);

// Bug 数据
const bugData = ref([]);
const searchQuery = ref('');
const loading = ref(false);
const totalCount = ref(0);
const currentPage = ref(1);
const pageSize = ref(10);

// 模拟获取 Bug 数据
const fetchBugData = async () => {
  loading.value = true;
  try {
    const params = {
      page: currentPage.value,
      per_page: pageSize.value,
      search: searchQuery.value
    };
    const response = await authApi.getAllBugReports(params);
    console.log('Backend response:', response);
    console.log('Response data:', response.data);

    if (response.data && Array.isArray(response.data.bugs)) {
      bugData.value = response.data.bugs.map(bug => ({
        bug_id: bug.id,
        title: bug.title,
        status: '待处理', // 你可能需要根据实际情况调整这个字段
        priority: bug.severity,
        description: bug.description,
        steps: bug.steps,
        expected: bug.expected,
        actual: bug.actual,
        environment: bug.environment,
        contact: bug.contact,
        attachment: bug.attachment
      }));
      totalCount.value = response.data.total_count;
    } else {
      console.error('Unexpected response format:', response.data);
      ElMessage.error('获取 Bug 报告失败：数据格式不正确');
    }
  } catch (error) {
    console.error('获取 Bug 报告失败:', error);
    ElMessage.error('获取 Bug 报告失败，请稍后重试');
  } finally {
    loading.value = false;
  }
};

// 查看 Bug 详情
const viewBug = (bug) => {
  console.log('查看 Bug:', bug);
  // 这里你可以实现查看 Bug 详情的逻辑，比如打开一个对话框显示详细信息
};

// 编辑 Bug
const editBug = (bug) => {
  console.log('编辑 Bug:', bug);
};

// 删除 Bug
const deleteBug = (bug) => {
  console.log('删除 Bug:', bug);
};

// 分页大小改变
const handleSizeChange = (val) => {
  pageSize.value = val;
  fetchBugData();
};

// 当前页改变
const handleCurrentChange = (val) => {
  currentPage.value = val;
  fetchBugData();
};

// 搜索输入变化时重置页码并重新获取数据
const handleSearch = () => {
  currentPage.value = 1; // 重置页码
  fetchBugData();
};

// 组件挂载时获取数据
onMounted(() => {
  fetchBugData();
});

// 添加状态和优先级的样式处理方法
const getStatusType = (status) => {
  switch (status) {
    case '待处理': return 'warning';
    case '处理中': return 'info';
    case '已解决': return 'success';
    default: return '';
  }
};

const getPriorityType = (priority) => {
  switch (priority) {
    case '高': return 'danger';
    case '中': return 'warning';
    case '低': return 'info';
    default: return '';
  }
};
</script>

<style scoped>
.mobile-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.mobile-card {
  margin-bottom: 10px;
  border-radius: var(--dca-radius);
  background-color: var(--dca-surface);
  border: 1px solid var(--dca-border);
  box-shadow: none;
}

.card-item {
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid var(--dca-border);
}

.card-item:last-child {
  border-bottom: none;
}

.card-item .label {
  width: 80px;
  color: var(--dca-text-muted);
  font-size: 14px;
}

.card-item .value {
  flex: 1;
  color: var(--dca-text);
  font-size: 14px;
  word-break: break-all;
}

.card-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid var(--dca-border);
}
</style>
